import React, { useMemo, useState } from 'react';

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { fetchMedicationSummaryList } from '../utils/fetchData';
import { useApi }  from '../hooks/useApi';
import Loading from '../components/Loading';
import ErrorDisplay from '../components/ErrorDisplay';

const MedicationPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { data: summaryList, loading, error } = useApi(fetchMedicationSummaryList);

  const filteredList = useMemo(() => 
    summaryList?.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [],
  [summaryList, searchTerm]);

  if (loading) return <Loading />;
  if (error) return <ErrorDisplay message={error} />;

  return (
    <div className="bg-white rounded-lg shadow p-6" style={{ margin: '3rem' }}>
      <div className='flex justify-between items-center mb-6'>
        <h1 className="text-2xl font-bold text-gray-800">{t('medicationPage.title')}</h1>
        <div className='grid items-center w-72'>
          <input 
            type='text' 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('medicationPage.search')}
            className='col-start-1 row-start-1 w-full pl-4 pr-12 py-2 bg-[#e0e5e9] text-gray-800 placeholder-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          <div className="col-start-1 row-start-1 justify-self-end flex items-center justify-center w-12 h-full bg-hero-bg rounded-r-lg pointer-events-none">
            <svg className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
      {/* Medication table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('medicationPage.tableHeaders.medicationName')}
              </th>
              <th className="w-40 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('medicationPage.tableHeaders.interactions')}
              </th>
              <th className="w-32 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('medicationPage.tableHeaders.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredList.map((summary) => (
                <tr key={summary.name}>
                  <td className="max-w-xs px-6 py-4 whitespace-nowrap font-medium text-gray-900 truncate">
                    <Link to={`/medication/${encodeURIComponent(summary.name)}`} className="text-current cursor-pointer">
                      {summary.name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">{summary.totalInteractions}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  <Link 
                    to={`/medication/${encodeURIComponent(summary.name)}`} 
                    className="font-sans font-medium text-sidebar-text hover:text-sidebar-accent transition-colors duration-200 uppercase"
                    >{t('medicationPage.tableContent.details')} &gt;
                  </Link>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MedicationPage;